import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Building2, CheckCircle2, UserPlus, Phone, Mail, MapPin } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { API } from "../App";

const PartnerFormPage = () => {
  const [searchParams] = useSearchParams();
  const partnerCode = searchParams.get("code") || "";

  const [form, setForm] = useState({
    name: "", phone: "", email: "", project_interest: "",
    configuration: "", budget_min: "", budget_max: "",
    locality: "", notes: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      setError("Name and phone number are required");
      return;
    }
    if (!partnerCode) {
      setError("Invalid partner link. Please contact your partner for the correct URL.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/leads/webhook/partner`, {
        partner_code: partnerCode,
        ...form,
        budget_min: form.budget_min ? parseFloat(form.budget_min) : undefined,
        budget_max: form.budget_max ? parseFloat(form.budget_max) : undefined,
      });
      setSubmitted(true);
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Invalid partner code. Please contact your partner.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-white border-slate-200 shadow-lg">
          <CardContent className="p-10 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 font-['Manrope'] mb-2">Thank You!</h2>
            <p className="text-slate-600 mb-6">Your enquiry has been submitted successfully. Our team will reach out to you shortly.</p>
            <Button onClick={() => { setSubmitted(false); setForm({ name: "", phone: "", email: "", project_interest: "", configuration: "", budget_min: "", budget_max: "", locality: "", notes: "" }); }} variant="outline">
              Submit Another Enquiry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4" data-testid="partner-form-page">
      <Card className="max-w-lg w-full bg-white border-slate-200 shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="w-14 h-14 bg-gradient-to-br from-slate-900 to-slate-700 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Building2 className="w-7 h-7 text-white" />
          </div>
          <CardTitle className="text-xl font-bold font-['Manrope']">Property Enquiry</CardTitle>
          <p className="text-sm text-slate-500 mt-1">Fill in your details and we'll get back to you</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg">{error}</div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Full Name *</label>
                <div className="relative">
                  <UserPlus className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <Input className="pl-10" placeholder="Your name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required data-testid="partner-name" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Phone *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                  <Input className="pl-10" placeholder="+91 98765 43210" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} required data-testid="partner-phone" />
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <Input className="pl-10" placeholder="you@example.com" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} data-testid="partner-email" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Project Interest</label>
                <Input placeholder="Project name" value={form.project_interest} onChange={(e) => setForm(f => ({ ...f, project_interest: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Configuration</label>
                <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" value={form.configuration} onChange={(e) => setForm(f => ({ ...f, configuration: e.target.value }))}>
                  <option value="">Select BHK</option>
                  <option value="1BHK">1 BHK</option>
                  <option value="2BHK">2 BHK</option>
                  <option value="3BHK">3 BHK</option>
                  <option value="4BHK+">4 BHK+</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Budget Min (₹)</label>
                <Input type="number" placeholder="e.g. 5000000" value={form.budget_min} onChange={(e) => setForm(f => ({ ...f, budget_min: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Budget Max (₹)</label>
                <Input type="number" placeholder="e.g. 10000000" value={form.budget_max} onChange={(e) => setForm(f => ({ ...f, budget_max: e.target.value }))} />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Preferred Locality</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <Input className="pl-10" placeholder="Area / Locality" value={form.locality} onChange={(e) => setForm(f => ({ ...f, locality: e.target.value }))} />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Additional Notes</label>
              <textarea className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm min-h-[80px] resize-none" placeholder="Any specific requirements..." value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>

            <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading} data-testid="partner-submit-btn">
              {loading ? "Submitting..." : "Submit Enquiry"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerFormPage;
